import {IssueBodyMarkdownViewer, IssueMarkdownContext} from '@github-ui/issue-body'
import type {ReactionViewerGroups$key, ReactionViewerUser$key} from '@github-ui/reaction-viewer'
import {ReactionViewer} from '@github-ui/reaction-viewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {Box} from '@primer/react'
import type React from 'react'
import {Suspense, useEffect, useRef, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {CLASS_NAMES, TEST_IDS, VALUES} from '../../constants'
import {getQuotedText} from '../../utils/quotes'
import {CommentLoading} from '../CommentLoading'
import type {IssueCommentViewerCommentRow$key} from './__generated__/IssueCommentViewerCommentRow.graphql'
import type {IssueCommentViewerGhostUserQuery as IssueCommentViewerGhostUserQueryType} from './__generated__/IssueCommentViewerGhostUserQuery.graphql'
import type {IssueCommentViewerMarkdownViewer$key} from './__generated__/IssueCommentViewerMarkdownViewer.graphql'
import {IssueCommentHeader} from './IssueCommentHeader'

type Props = {
  comment: IssueCommentViewerCommentRow$key
  reactionGroups: ReactionViewerGroups$key
  viewer: ReactionViewerUser$key
  setIsEditing: () => void
  onReply: (quotedComment: string) => void
  onLinkClick?: (event: MouseEvent) => void
  refAttribute?: React.RefObject<HTMLDivElement>
  highlightedCommentId?: string
  navigate: (url: string) => void
}

type InternalProps = Props & {
  login: string
  avatarUrl: string
}

export const IssueCommentFragment = graphql`
  fragment IssueCommentViewerCommentRow on IssueComment {
    id
    databaseId
    ...IssueCommentViewerMarkdownViewer
    ...IssueCommentHeader
    author {
      avatarUrl
      login
    }
    issue {
      locked
    }
    body
    isHidden: isMinimized
    viewerCanUpdate
  }
`

const IssueCommentViewerMarkdownViewer = graphql`
  fragment IssueCommentViewerMarkdownViewer on IssueComment {
    id
    body
    bodyHTML(unfurlReferences: true)
    viewerCanUpdate
  }
`

export function IssueCommentViewer({comment, ...rest}: Props) {
  const data = useFragment(IssueCommentFragment, comment)
  const withAuthor = data.author != null
  return withAuthor ? (
    <IssueCommentViewerInternal
      comment={comment}
      {...rest}
      login={data.author.login}
      avatarUrl={data.author.avatarUrl}
    />
  ) : (
    <Suspense fallback={<CommentLoading />}>
      <IssueCommentViewerInternalWithoutAuthor comment={comment} {...rest} />
    </Suspense>
  )
}

function IssueCommentViewerInternalWithoutAuthor(props: Props) {
  const data = useLazyLoadQuery<IssueCommentViewerGhostUserQueryType>(
    graphql`
      query IssueCommentViewerGhostUserQuery($ghostLogin: String!) {
        user(login: $ghostLogin) {
          login
          avatarUrl
        }
      }
    `,
    {ghostLogin: VALUES.ghostUserLogin},
    {fetchPolicy: 'store-or-network'},
  )
  if (data.user == null) {
    throw new Error('Ghost user not found')
  }
  return <IssueCommentViewerInternal {...props} login={data.user.login} avatarUrl={data.user.avatarUrl} />
}

function IssueCommentViewerInternal({
  comment,
  reactionGroups,
  viewer,
  setIsEditing,
  onLinkClick,
  onReply,
  refAttribute,
  login,
  avatarUrl,
  highlightedCommentId,
  navigate,
}: InternalProps) {
  const commentData = useFragment(IssueCommentFragment, comment)

  const highlighted = String(commentData.databaseId) === highlightedCommentId
  const [isMinimized, setIsMinimized] = useState(commentData.isHidden)

  // sync external mark as hidden with internal state
  useEffect(() => {
    if (isMinimized === commentData.isHidden) return
    setIsMinimized(commentData.isHidden)

    // we wish to sync only when external isHidden changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commentData.isHidden])

  const handleOnReply = (quotedText?: string) => {
    onReply(quotedText || getQuotedText(commentData.body))
  }

  const commentRef = useRef<HTMLDivElement>(null)

  return (
    <>
      <Box sx={{display: 'flex', flexDirection: 'row', gap: 2}} className={CLASS_NAMES.issueComment} ref={commentRef}>
        <Box
          data-testid={TEST_IDS.commentViewerOuterBox(commentData.id)}
          sx={{
            overflowX: 'auto',
            scrollMarginTop: '100px',
            flexGrow: 1,
          }}
          ref={highlighted ? refAttribute : null}
        >
          <Box sx={{display: 'flex', flexDirection: 'column', mb: isMinimized ? 0 : 3, ml: 3, mt: 0, mr: 2, gap: 2}}>
            <IssueCommentHeader
              comment={commentData}
              login={login}
              navigate={navigate}
              editComment={setIsEditing}
              onReplySelect={handleOnReply}
              isMinimized={isMinimized}
              onMinimize={setIsMinimized}
              avatarUrl={avatarUrl}
              commentRef={commentRef}
            />
            {(!commentData.isHidden || !isMinimized) && (
              <>
                <CommentMarkdownViewer comment={commentData} onLinkClick={onLinkClick} />
                <ReactionViewer
                  subjectId={commentData.id}
                  reactionGroups={reactionGroups}
                  viewer={viewer}
                  locked={commentData.issue.locked}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

type CommentMarkdownViewerProps = {
  comment: IssueCommentViewerMarkdownViewer$key
  onLinkClick?: (event: MouseEvent) => void
}

function CommentMarkdownViewer({comment, onLinkClick}: CommentMarkdownViewerProps) {
  const {id, bodyHTML, viewerCanUpdate, body} = useFragment(IssueCommentViewerMarkdownViewer, comment)
  const html = (bodyHTML ?? '') as SafeHTMLString
  return (
    <IssueBodyMarkdownViewer
      id={id}
      context={IssueMarkdownContext.Comment}
      html={html}
      markdown={body}
      viewerCanUpdate={viewerCanUpdate}
      onLinkClick={onLinkClick}
    />
  )
}

try{ IssueCommentViewer.displayName ||= 'IssueCommentViewer' } catch {}
try{ IssueCommentViewerInternalWithoutAuthor.displayName ||= 'IssueCommentViewerInternalWithoutAuthor' } catch {}
try{ IssueCommentViewerInternal.displayName ||= 'IssueCommentViewerInternal' } catch {}
try{ CommentMarkdownViewer.displayName ||= 'CommentMarkdownViewer' } catch {}